import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  InputAdornment,
  IconButton,
  Link as MuiLink,
  Snackbar,
} from '@mui/material';
import { Visibility, VisibilityOff, Person, Lock, Business, ArrowBack } from '@mui/icons-material';
import { API_BASE_URL } from '../api';
import { AuthContext } from '../components/AuthContext';
import { tokens } from '../theme/theme';

const LoginPage = () => {
  const navigate = useNavigate();
  const { syncFromStorage } = useContext(AuthContext);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    companyName: '' // Optional: for company-specific login
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [resetToastOpen, setResetToastOpen] = useState(false);

  useEffect(() => {
    const msg = sessionStorage.getItem('loginPasswordResetToast');
    if (msg) {
      sessionStorage.removeItem('loginPasswordResetToast');
      setResetToastOpen(true);
    }
  }, []);

  const updateFormData = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.username) newErrors.username = 'Username is required';
    if (!formData.password) newErrors.password = 'Password is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    setErrors({});

    try {
      const response = await fetch(`${API_BASE_URL}/api/multi-tenant-login/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: formData.username,
          password: formData.password,
          company_name: formData.companyName || null
        })
      });

      const text = await response.text();
      let data;
      try {
        data = text ? JSON.parse(text) : null;
      } catch {
        // Server returned HTML (404, 500, etc.) instead of JSON
        setErrors({
          submit: `Login failed (${response.status}). The API may be down or returning an error page.`
        });
        return;
      }

      if (response.ok) {
        const userData = {
          ...data.user,
          company_id: data.company?.id,
          company_name: data.company?.name || '',
        };
        localStorage.setItem('user', JSON.stringify(userData));
        localStorage.setItem('access_token', data.access_token || data.access);
        if (data.refresh_token || data.refresh) {
          localStorage.setItem('refresh_token', data.refresh_token || data.refresh);
        }
        await syncFromStorage();
        navigate('/dashboard');
      } else {
        setErrors({ submit: data?.error || 'Login failed. Please check your credentials.' });
      }
    } catch {
      setErrors({ submit: 'Network error. Please check your connection and try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', flexDirection: 'column', justifyContent: 'center', py: 4, px: 2 }}>
      <Snackbar
        open={resetToastOpen}
        autoHideDuration={6000}
        onClose={() => setResetToastOpen(false)}
        message="Password reset successfully"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
      <Box sx={{ mx: 'auto', maxWidth: 400, width: '100%' }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/')}
            sx={{ color: 'text.secondary' }}
          >
            Back to Home
          </Button>
        </Box>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>Welcome Back</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Sign in to your company workspace</Typography>
        </Box>
      </Box>
      <Box component="form" onSubmit={handleSubmit} sx={{ mx: 'auto', maxWidth: 400, width: '100%' }} aria-label="Sign in form">
        <Box sx={{ bgcolor: 'background.paper', p: 4, borderRadius: 2, border: '1px solid', borderColor: 'divider', display: 'flex', flexDirection: 'column', gap: 3 }} role="main">
          <TextField fullWidth label="Company Name (Optional)" placeholder="Your company name" value={formData.companyName} onChange={(e) => updateFormData('companyName', e.target.value)} size="small" InputProps={{ startAdornment: <Business sx={{ color: 'text.secondary', mr: 1 }} /> }} />
          <TextField fullWidth label="Username" required error={!!errors.username} helperText={errors.username} placeholder="Enter your username" value={formData.username} onChange={(e) => updateFormData('username', e.target.value)} size="small" InputProps={{ startAdornment: <Person sx={{ color: 'text.secondary', mr: 1 }} /> }} />
          <TextField fullWidth label="Password" type={showPassword ? 'text' : 'password'} required error={!!errors.password} helperText={errors.password} placeholder="Enter your password" value={formData.password} onChange={(e) => updateFormData('password', e.target.value)} size="small" InputProps={{ startAdornment: <Lock sx={{ color: 'text.secondary', mr: 1 }} />, endAdornment: <InputAdornment position="end"><IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small" aria-label={showPassword ? 'Hide password' : 'Show password'}>{showPassword ? <VisibilityOff /> : <Visibility />}</IconButton></InputAdornment> }} />
          {errors.submit && <Alert severity="error">{errors.submit}</Alert>}
          <Button type="submit" variant="contained" fullWidth disabled={isSubmitting} sx={{ py: 1.5, textTransform: 'none', fontWeight: 600, borderRadius: 2 }}>{isSubmitting ? 'Signing in...' : 'Sign in'}</Button>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <MuiLink
              component={Link}
              to="/forgot-password"
              variant="body2"
              sx={{ fontSize: 13, color: tokens.color.ink3, fontWeight: 500 }}
            >
              Forgot password?
            </MuiLink>
            <Typography variant="body2" color="text.secondary">Don't have an account? <MuiLink component={Link} to="/register" sx={{ fontWeight: 500 }}>Register</MuiLink></Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default LoginPage;